import { Box, Link, Typography } from '@mui/material'
import { Stack } from '@mui/system'
import { useEffect, useRef } from 'react'
import QRCode from 'qrcode'
import { useBranch } from '../../context/branch_context'
import { ClientDocumentType } from '../../context/client_context'
import { useConfigs } from '../../context/configs_context'
import { ParcelInfo, SaleInfo } from '../../context/sale_context'
import {
    getDateInDDMMYYYYFormat,
    getDateString,
    maskCurrency,
    numeroPorExtensoEspanhol,
} from '../../utils/maks'

interface ParcelNoteProps {
    sale: SaleInfo
    totalParcels: number
    parcel: ParcelInfo
    clientVia: boolean
}

function ParcelNote({
    sale,
    totalParcels,
    parcel,
    clientVia,
}: ParcelNoteProps) {
    const { configs, contactTypes, base64Logo } = useConfigs()
    const { branches } = useBranch()

    const contacts = sale.branch ? sale.branch.contacts : configs.contacts
    const companyName =
        sale.branch && configs.showBranchNameInNote
            ? sale.branch.name
            : configs.companyName
    const companyLogo = sale.branch?.companyLogo
        ? branches.find(b => b.id === sale.branch?.id)?.companyLogo?.content
        : base64Logo
    const companySlogan = sale.branch?.companySlogan
        ? sale.branch.companySlogan
        : configs.companySlogan
    const companyDocument = sale.branch?.companyDocument
        ? sale.branch.companyDocument
        : configs.companyDocument

    const isEletronicNote = !!parcel.qrCode
    const qrCodeRef = useRef<HTMLCanvasElement | null>(null)

    useEffect(() => {
        if (parcel.qrCode)
            QRCode.toCanvas(qrCodeRef.current, parcel.qrCode, { width: 200 })
                .catch(() => {})
    }, [parcel.qrCode])

    return (
        <Stack
            direction="column"
            width="100%"
            px="20px"
            py="10px"
            height="50vh"
        >
            <Stack direction={'row'} width={'100%'}>
                <Stack
                    direction={'column'}
                    border={'1px solid black'}
                    borderRight={'0'}
                    borderRadius={'12px'}
                    width={'70%'}
                >
                    <Stack direction={'row'} p={'10px'}>
                        <Box
                            component={'img'}
                            src={companyLogo}
                            width={'220px'}
                            height={'110px'}
                            zIndex={'-1'}
                        />
                        <Stack textAlign={'center'} flex={'1'} px={'15px'}>
                            <Typography
                                fontSize={'14px'}
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {companyName}
                            </Typography>
                            <Typography
                                fontSize={'9px'}
                                sx={{ wordBreak: 'break-word' }}
                                fontStyle={'italic'}
                                whiteSpace={'pre-line'}
                            >
                                {`${companySlogan}`}
                            </Typography>
                            <Typography
                                fontSize={'8px'}
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {contactTypes
                                    .filter((ct) =>
                                        contacts.find(
                                            (c) => c.contactType.id === ct.id
                                        )
                                    )
                                    .map(
                                        (ct) =>
                                            `${ct.name}: ${contacts
                                                .filter(
                                                    (c) =>
                                                        c.contactType.id ===
                                                        ct.id
                                                )
                                                .map((c) => c.value)
                                                .join(', ')}`
                                    )
                                    .join('; ')}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
                <Stack
                    direction={'column'}
                    border={'1px solid black'}
                    borderRadius={'12px'}
                    width={'30%'}
                >
                    <Stack direction={'row'} p={'10px'} textAlign={'center'}>
                        <Box flex={'1'}>
                            <Typography fontSize={'11px'}>
                                TIMBRADO N {parcel.noteTimbrado ?? '---'}
                            </Typography>
                            <Typography fontSize={'11px'} mt={'1px'}>
                                Série: {parcel.notePrefix ?? '---'}
                            </Typography>
                            <Typography fontSize={'11px'} mt={'1px'}>
                                RUC: {companyDocument}
                            </Typography>
                            <Typography fontSize={'11px'} mt={'1px'}>
                                Fecha início vigência:{' '}
                                {parcel.noteIniD
                                    ? getDateInDDMMYYYYFormat(
                                          new Date(parcel.noteIniD)
                                      )
                                    : '---'}
                            </Typography>
                            <Typography fontSize={'11px'}>
                                Fecha fin vigência:{' '}
                                {parcel.noteEndD
                                    ? getDateInDDMMYYYYFormat(
                                          new Date(parcel.noteEndD)
                                      )
                                    : '---'}
                            </Typography>
                            <Typography
                                fontSize={'14px'}
                                mt={'5px'}
                                fontWeight={'bold'}
                                textAlign={'center'}
                            >
                                {parcel.numeroFaturaLegal ?? '---'}
                            </Typography>
                            <Typography
                                fontSize={'16px'}
                                fontWeight={'bold'}
                                textAlign={'center'}
                            >
                                {isEletronicNote
                                    ? 'FACTURA ELECTRÓNICA'
                                    : 'FACTURA'}
                            </Typography>
                            <Typography fontSize={'12px'} mt={'3px'}>
                                {clientVia ? 'VIA CLIENTE' : 'ORIGINAL'}
                            </Typography>
                            <Typography
                                fontSize={'22px'}
                                mt={'3px'}
                                fontWeight={'bold'}
                            >
                                {maskCurrency(parcel.total, sale.coin)}
                            </Typography>
                        </Box>
                    </Stack>
                </Stack>
            </Stack>
            <Typography fontSize={'18px'} my={'15px'} textAlign={'center'}>
                COBRO DE CUOTA
            </Typography>
            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderRadius={'12px'}
                direction={'column'}
                padding={'10px'}
            >
                <Stack direction={'row'} alignItems={'center'}>
                    <Box width={'70%'}>
                        <Typography fontSize={'16px'} fontWeight={'bold'}>
                            Nombre: {sale.client?.name || '---'}
                        </Typography>
                        <Typography fontSize={'16px'} fontWeight={'bold'}>
                            {sale.client?.documentType ===
                            ClientDocumentType.RUC
                                ? 'Ruc'
                                : 'Documento'}
                            : {sale.client?.document || '---'}
                        </Typography>
                    </Box>
                    <Box>
                        <Box>
                            <Typography fontSize={'11px'}>
                                Fecha:{' '}
                                {getDateString({
                                    date: sale.date,
                                    hour: sale.hour,
                                    configs: configs,
                                    isSummerTime: !!sale.isSummerTime,
                                })}
                            </Typography>
                            <Typography fontSize={'11px'}>
                                Telefonos:{' '}
                                {sale.client?.phoneNumbers.join(', ') || '---'}
                            </Typography>
                            <Typography fontSize={'11px'}>
                                Dirección: {sale.client?.address || '---'}
                            </Typography>
                        </Box>
                    </Box>
                </Stack>
            </Stack>
            <Typography fontSize={'14px'} mt={'10px'} mb={'3px'}>
                Concept
            </Typography>
            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderRadius={'12px'}
                direction={'column'}
                justifyContent={'space-between'}
                p={'10px'}
            >
                <Typography fontSize={'14px'}>
                    (FACTURA NO. :.F.) CUOTA:{parcel.parcelNumber}/
                    {totalParcels} Nro.:({parcel.numeroFaturaLegal ?? '---'}) de{' '}
                    {sale.client?.name}
                </Typography>
            </Stack>
            <Typography fontSize={'14px'} mt={'10px'} mb={'3px'}>
                Detalles del pago
            </Typography>
            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderRadius={'12px'}
                p={'10px'}
            >
                <Stack width={'100%'} direction={'row'} spacing={'80px'}>
                    <Typography fontSize={'11px'}>Capital: 0</Typography>
                    <Typography fontSize={'11px'}>
                        Interés: {maskCurrency(parcel.totalFees, sale.coin)}
                    </Typography>
                    <Typography fontSize="16px" fontWeight="bold">
                        Mora: {maskCurrency(parcel.total, sale.coin)}
                    </Typography>
                </Stack>
                <Typography fontSize={'11px'}>
                    En letras: {numeroPorExtensoEspanhol(parcel?.total, sale.coin)}
                </Typography>
            </Stack>
            <Stack
                width={'100%'}
                flex={'1'}
                direction={'column'}
                justifyContent={'flex-end'}
            >
                <Stack
                    direction={'row'}
                    justifyContent={'space-evenly'}
                    width={'100%'}
                    p={'3px'}
                >
                    <Typography fontSize={'12px'}>Sello</Typography>
                    <Typography fontSize={'12px'}>Firma</Typography>
                </Stack>
                <Box width="100%" borderBottom="1px solid black" />
            </Stack>
            {isEletronicNote && (
                <Stack
                    width={'100%'}
                    border={'1px solid black'}
                    borderRadius={'12px'}
                    p="10px"
                    mt="10px"
                >
                    <Stack direction={'row'} spacing={'15px'}>
                        <Box component="canvas" ref={qrCodeRef} />
                        <Stack
                            direction="column"
                            py="10px"
                            justifyContent="space-between"
                        >
                            <Stack>
                                <Typography fontSize="12px">
                                    Consulte la validez de esta factura
                                    eletrónica con el documento CDC impreso en:
                                </Typography>
                                <Link
                                    underline="hover"
                                    href="https://ekuatia.set.gov.py/consultas/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    fontSize="13px"
                                    sx={{ color: 'inherit' }}
                                >
                                    https://ekuatia.set.gov.py/consultas/
                                </Link>
                                <Typography fontSize="13px">
                                    CDC:{' '}
                                    {parcel.qrCode ? (
                                        <Link
                                            underline="hover"
                                            href={parcel.qrCode}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ color: 'inherit' }}
                                        >
                                            {parcel.cdc}
                                        </Link>
                                    ) : (
                                        parcel.cdc
                                    )}
                                </Typography>
                            </Stack>
                            <Stack>
                                <Typography fontSize="11px">
                                    ESTE DOCUMENTO ES UNA REPRESENTACIÓN GRÁFICA
                                    DE UN DOCUMENTO ELETRÓNICO (XML)
                                </Typography>
                                <Typography fontWeight="bold" fontSize="12px">
                                    Información de interés del facturador
                                    eletrónico emisor
                                </Typography>
                                <Typography fontSize="11px">
                                    Si su documento eletrónico presenta algún
                                    error, podrá solicitar la modifcación dentro
                                    de las 72 horas siguientes de la emisión de
                                    éste comprobante
                                </Typography>
                            </Stack>
                        </Stack>
                    </Stack>
                </Stack>
            )}
        </Stack>
    )
}

interface Props {
    sale: SaleInfo
    totalParcels: number
    parcels: ParcelInfo[]
    handleClose(): void
    dontPrint?: boolean
}

export function ParcelNoteDialog({
    handleClose,
    sale,
    totalParcels,
    parcels,
    dontPrint,
}: Props) {
    useEffect(() => {
        if (dontPrint) return
        setTimeout(() => {
            window.scroll(0, 0)
            window.print()
            handleClose()
        }, 100)
    }, [])

    return (
        <Box
            id="note"
            className={dontPrint ? undefined : 'printable'}
            style={dontPrint ? { display: 'block' } : undefined}
        >
            <Stack direction={'column'}>
                {parcels.map((parcel) => (
                    <ParcelNote
                        sale={sale}
                        totalParcels={totalParcels}
                        parcel={parcel}
                        clientVia={true}
                    />
                ))}
            </Stack>
        </Box>
    )
}
